import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Plus, Trash2, Target, ArrowRight, Activity, Scale, Ruler } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { SaveResultButton } from '../components/tools/SaveResultButton';
import { EmailResultsGate } from '../components/tools/EmailResultsGate';

const STORAGE_KEY = 'ninja_body_recomp_entries';

const getEntries = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
};
const persistEntries = (entries) => localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));

const MiniChart = ({ data, color, label }) => {
  if (data.length < 2) return null;
  const vals = data.map(d => d.value).filter(v => v > 0);
  if (vals.length < 2) return null;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const h = 60;
  const w = 200;
  const points = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 10) - 5;
    return `${x},${y}`;
  }).join(' ');
  return (
    <div className="bg-zinc-800/50 border border-zinc-700 p-3">
      <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading mb-2">{label}</p>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ maxHeight: '60px' }}>
        <polyline fill="none" stroke={color} strokeWidth="2" points={points} />
        {vals.map((v, i) => {
          const x = (i / (vals.length - 1)) * w;
          const y = h - ((v - min) / range) * (h - 10) - 5;
          return <circle key={v} cx={x} cy={y} r="3" fill={color} />;
        })}
      </svg>
      <div className="flex justify-between text-zinc-600 text-[10px] mt-1">
        <span>{vals[0]}</span>
        <span>{vals[vals.length - 1]}</span>
      </div>
    </div>
  );
};

export default function BodyRecompPage() {
  const [entries, setEntries] = useState(getEntries);
  const [weight, setWeight] = useState('');
  const [bodyFat, setBodyFat] = useState('');
  const [notes, setNotes] = useState('');
  const [goalWeight, setGoalWeight] = useState('');
  const [goalBf, setGoalBf] = useState('');
  const [saved, setSaved] = useState(false);
  const { saveResult } = useResults();

  const addEntry = useCallback(() => {
    const w = parseFloat(weight);
    const bf = parseFloat(bodyFat);
    if (!w || w <= 0) return;
    const entry = {
      id: Date.now(),
      date: new Date().toISOString().slice(0, 10),
      weight: w,
      bodyFat: bf > 0 ? bf : null,
      leanMass: bf > 0 ? Math.round(w * (1 - bf / 100) * 10) / 10 : null,
      fatMass: bf > 0 ? Math.round(w * (bf / 100) * 10) / 10 : null,
      notes: notes.trim(),
    };
    const updated = [entry, ...entries];
    setEntries(updated);
    persistEntries(updated);
    setWeight('');
    setBodyFat('');
    setNotes('');
  }, [weight, bodyFat, notes, entries]);

  const deleteEntry = useCallback((id) => {
    const updated = entries.filter(e => e.id !== id);
    setEntries(updated);
    persistEntries(updated);
  }, [entries]);

  const clearAll = () => {
    setEntries([]);
    persistEntries([]);
  };

  const stats = useMemo(() => {
    if (entries.length === 0) return null;
    const latest = entries[0];
    const oldest = entries[entries.length - 1];
    const weightChange = latest.weight - oldest.weight;
    const bfChange = latest.bodyFat && oldest.bodyFat ? latest.bodyFat - oldest.bodyFat : null;
    const leanChange = latest.leanMass && oldest.leanMass ? latest.leanMass - oldest.leanMass : null;
    const fatChange = latest.fatMass && oldest.fatMass ? latest.fatMass - oldest.fatMass : null;
    const gw = parseFloat(goalWeight);
    const gb = parseFloat(goalBf);
    const weightToGoal = gw > 0 ? gw - latest.weight : null;
    const bfToGoal = gb > 0 && latest.bodyFat ? gb - latest.bodyFat : null;
    return { latest, oldest, weightChange, bfChange, leanChange, fatChange, weightToGoal, bfToGoal, totalEntries: entries.length };
  }, [entries, goalWeight, goalBf]);

  const chartData = useMemo(() => {
    const reversed = [...entries].reverse();
    return {
      weight: reversed.map(e => ({ value: e.weight })),
      bodyFat: reversed.filter(e => e.bodyFat).map(e => ({ value: e.bodyFat })),
      leanMass: reversed.filter(e => e.leanMass).map(e => ({ value: e.leanMass })),
    };
  }, [entries]);

  const handleSave = () => {
    if (!stats) return;
    saveResult('bodyRecomp', {
      value: `${stats.latest.weight} lbs`,
      label: stats.latest.bodyFat ? `${stats.latest.bodyFat}% BF` : 'Weight tracked',
      entries: stats.totalEntries,
      weightChange: stats.weightChange ? `${stats.weightChange > 0 ? '+' : ''}${stats.weightChange.toFixed(1)} lbs` : null,
    });
    setSaved(true);
  };

  const Trend = ({ value, invert = false }) => {
    if (value === null || value === undefined) return null;
    const isPositive = invert ? value < 0 : value > 0;
    const isNeutral = Math.abs(value) < 0.1;
    if (isNeutral) return <span className="text-zinc-500 text-xs flex items-center gap-1"><Minus className="w-3 h-3" /> No change</span>;
    return (
      <span className={`text-xs flex items-center gap-1 ${isPositive ? 'text-green-500' : 'text-red-400'}`}>
        {value > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {value > 0 ? '+' : ''}{value.toFixed(1)}
      </span>
    );
  };

  return (
    <main className="pt-20 min-h-screen" data-testid="body-recomp-page">
      <section className="container-ninja section-spacing">
        <div className="max-w-3xl mx-auto">
          <Link to="/tools" className="text-zinc-500 text-xs font-heading uppercase tracking-wider hover:text-white transition-colors inline-flex items-center gap-1 mb-6" data-testid="back-to-tools">
            <ArrowLeft className="w-3 h-3" /> All Tools
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center bg-[#00F0FF]/10 border border-[#00F0FF]/30">
              <Activity className="w-5 h-5 text-[#00F0FF]" />
            </div>
            <h1 className="text-3xl sm:text-4xl text-white font-heading" data-testid="page-title">Body Recomp Tracker</h1>
          </div>
          <p className="text-zinc-400 text-sm mb-8">Track body weight, body fat %, lean mass, and fat mass over time. All data stored locally.</p>

          {/* Goal Setting */}
          <div className="bg-zinc-900/80 border border-zinc-800 p-5 mb-6" data-testid="goal-section">
            <h3 className="text-white font-heading text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-[#00F0FF]" /> Goals (Optional)
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Target Weight (lbs)</label>
                <input type="number" value={goalWeight} onChange={e => setGoalWeight(e.target.value)} placeholder="e.g. 180"
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#00F0FF] focus:outline-none" data-testid="input-goal-weight" />
              </div>
              <div>
                <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Target Body Fat %</label>
                <input type="number" value={goalBf} onChange={e => setGoalBf(e.target.value)} placeholder="e.g. 12"
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#00F0FF] focus:outline-none" data-testid="input-goal-bf" />
              </div>
            </div>
          </div>

          {/* Add Entry */}
          <div className="bg-zinc-900/80 border border-zinc-800 p-5 mb-6" data-testid="add-entry-section">
            <h3 className="text-white font-heading text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4 text-[#00F0FF]" /> Log Entry
            </h3>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div>
                <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Weight (lbs) *</label>
                <input type="number" value={weight} onChange={e => setWeight(e.target.value)} placeholder="e.g. 185" min="0"
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#00F0FF] focus:outline-none" data-testid="input-weight" />
              </div>
              <div>
                <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Body Fat %</label>
                <input type="number" value={bodyFat} onChange={e => setBodyFat(e.target.value)} placeholder="e.g. 15" min="0" max="60"
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#00F0FF] focus:outline-none" data-testid="input-bodyfat" />
              </div>
              <div>
                <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Notes</label>
                <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. post-cut"
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#00F0FF] focus:outline-none" data-testid="input-notes" />
              </div>
            </div>
            <button onClick={addEntry} disabled={!weight}
              className="w-full py-2.5 bg-[#00F0FF] text-black font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-30 flex items-center justify-center gap-2"
              data-testid="add-entry-btn">
              <Plus className="w-4 h-4" /> Log Entry
            </button>
          </div>

          {/* Stats & Charts — gated behind email after first entry */}
          {stats && (
            <div data-testid="stats-section">
              <EmailResultsGate
                toolId="body-recomp"
                toolName="Body Recomp Trends"
                resultPayload={{
                  total_entries: stats.totalEntries,
                  current_weight: stats.latest.weight,
                  current_bf: stats.latest.bodyFat,
                  weight_change: stats.weightChange,
                  bf_change: stats.bfChange,
                  lean_change: stats.leanChange,
                  fat_change: stats.fatChange,
                }}
                ctaLabel="Unlock my trends & charts"
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-zinc-900/80 border border-zinc-800 p-4 text-center">
                    <p className="text-2xl font-heading text-white" data-testid="stat-weight">{stats.latest.weight}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">Current (lbs)</p>
                    <Trend value={stats.weightChange} />
                  </div>
                  <div className="bg-zinc-900/80 border border-zinc-800 p-4 text-center">
                    <p className="text-2xl font-heading text-[#00F0FF]" data-testid="stat-bf">{stats.latest.bodyFat || '—'}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">Body Fat %</p>
                    <Trend value={stats.bfChange} invert />
                  </div>
                  <div className="bg-zinc-900/80 border border-zinc-800 p-4 text-center">
                    <p className="text-2xl font-heading text-green-500" data-testid="stat-lean">{stats.latest.leanMass || '—'}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">Lean Mass (lbs)</p>
                    <Trend value={stats.leanChange} />
                  </div>
                  <div className="bg-zinc-900/80 border border-zinc-800 p-4 text-center">
                    <p className="text-2xl font-heading text-[#E63946]" data-testid="stat-fat">{stats.latest.fatMass || '—'}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">Fat Mass (lbs)</p>
                    <Trend value={stats.fatChange} invert />
                  </div>
                </div>

                {/* Goal Progress */}
                {(stats.weightToGoal !== null || stats.bfToGoal !== null) && (
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {stats.weightToGoal !== null && (
                      <div className="bg-zinc-900/50 border border-[#00F0FF]/20 p-4">
                        <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading mb-1">Weight to Goal</p>
                        <p className="text-xl font-heading text-white" data-testid="goal-weight-remaining">{stats.weightToGoal > 0 ? '+' : ''}{stats.weightToGoal.toFixed(1)} lbs</p>
                        <p className="text-zinc-600 text-xs">{stats.weightToGoal > 0 ? 'to gain' : 'to lose'} (goal: {goalWeight} lbs)</p>
                      </div>
                    )}
                    {stats.bfToGoal !== null && (
                      <div className="bg-zinc-900/50 border border-[#00F0FF]/20 p-4">
                        <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading mb-1">BF% to Goal</p>
                        <p className="text-xl font-heading text-white" data-testid="goal-bf-remaining">{stats.bfToGoal > 0 ? '+' : ''}{stats.bfToGoal.toFixed(1)}%</p>
                        <p className="text-zinc-600 text-xs">{stats.bfToGoal > 0 ? 'to gain' : 'to drop'} (goal: {goalBf}%)</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Mini Charts */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="charts-section">
                  <MiniChart data={chartData.weight} color="#ffffff" label="Weight Trend" />
                  <MiniChart data={chartData.bodyFat} color="#00F0FF" label="Body Fat % Trend" />
                  <MiniChart data={chartData.leanMass} color="#22c55e" label="Lean Mass Trend" />
                </div>

                <SaveResultButton onSave={handleSave} saved={saved} />
              </EmailResultsGate>
            </div>
          )}

          {/* Entry Log */}
          {entries.length > 0 && (
            <div className="mt-6" data-testid="entry-log">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-heading text-sm uppercase tracking-wider">Log ({entries.length} entries)</h3>
                <button onClick={clearAll} className="text-zinc-600 text-xs hover:text-red-400 transition-colors flex items-center gap-1" data-testid="clear-all-btn">
                  <Trash2 className="w-3 h-3" /> Clear All
                </button>
              </div>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {entries.map(e => (
                  <div key={e.id} className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 px-4 py-3 group" data-testid={`entry-${e.id}`}>
                    <div className="flex items-center gap-4">
                      <span className="text-zinc-500 text-xs font-mono">{e.date}</span>
                      <span className="text-white text-sm font-heading">{e.weight} lbs</span>
                      {e.bodyFat && <span className="text-[#00F0FF] text-xs">{e.bodyFat}% BF</span>}
                      {e.leanMass && <span className="text-green-500 text-xs">{e.leanMass} lean</span>}
                      {e.notes && <span className="text-zinc-600 text-xs italic">{e.notes}</span>}
                    </div>
                    <button onClick={() => deleteEntry(e.id)} className="text-zinc-700 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100" data-testid={`delete-entry-${e.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {entries.length === 0 && (
            <div className="text-center py-12 text-zinc-600 text-sm" data-testid="empty-state">
              <Scale className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
              <p>No entries yet. Log your first weigh-in above.</p>
            </div>
          )}

          {/* Related Links */}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/tools/body-fat-calculator" className="text-[#00F0FF] text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="link-bf-calc">
              Body Fat Calculator <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to="/tools/macro-calculator" className="text-[#00F0FF] text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1 hover:text-white transition-colors">
              Macro Calculator <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to="/tools/tdee-calculator" className="text-[#00F0FF] text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1 hover:text-white transition-colors">
              TDEE Calculator <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
